<template>
  <form id="payment-info" class="stripe-payment-form" @submit.prevent>
    <div class="field-row">
      <div class="field-label">
        <p class="field-title">Card Number</p>
        <p class="field-required">(Required)</p>
      </div>
      <div id="card-number" class="field-shell field-shell-card" />
    </div>

    <div class="field-row">
      <div class="field-label">
        <p class="field-title">Cardholder Name</p>
        <p class="field-required">(Required)</p>
      </div>
      <div class="field-shell field-shell-input">
        <input v-model="cardholderName" placeholder="Cardholder Name" class="payment-input" />
      </div>
    </div>

    <div class="split-fields-row">
      <div class="field-label">
        <p class="field-title">Expiration Date</p>
        <p class="field-required">(Required)</p>
      </div>
      <div id="card-expiry" class="field-shell field-shell-card" />

      <div class="field-label split-label">
        <p class="field-title">CVV</p>
        <p class="field-required">(Required)</p>
      </div>
      <div id="card-cvv" class="field-shell field-shell-card" />
    </div>

    <div class="field-row">
      <div></div>
      <div
        v-if="showSaveCard"
        id="save-card-future-use"
        class="flex cursor-pointer items-center"
        @click="isSaveCard = !isSaveCard"
      >
        <span class="material-icons-outlined check-box">
          {{ isSaveCard ? "check_box" : "check_box_outline_blank" }}
        </span>
        <p>Save card for future use</p>
      </div>
    </div>

    <div class="action-row">
      <NewLibraryButton v-if="showBackButton" color-type="dark-blue-stroke" class="action-back-btn" @click="$emit('cancelPayment')">Back</NewLibraryButton>
      <button id="submit" v-loading.lock="completePaymentLoading" :disabled="disabled" :class="[isBottom ? 'bottom-fixed' : '', showBackButton ? 'with-back-button' : 'full-width', { 'is-disabled': disabled }]">
        <div v-if="mode === 'payment'">Complete Payment {{currency.symbol + chargeAmount.toFixed(2)}}</div>
        <div v-else>Save Card</div>
      </button>
    </div>
  </form>
</template>

<script>
import NewLibraryButton from "@/NewLibrary/Button.vue";
import { getStripeKey, createPaymentIntent, chargeApi, payOrdersApi, savePaymentApi } from '@/api/order-page/newChargingService';
import { generateOrderIdApi } from '@/api/orderkit';
import { getChargeDetailsForPayOrdersApi, getOrderSamplePriceMaps } from '.';
import { notify } from '@/utils/notify';
export default {
  components: {
    NewLibraryButton
  },
  props: {
    formId: {
      type: String,
      default: 'stripe-card-form',
    },
    chargeAmount: {
      type: Number,
      default: 0
    },
    mode: {
      type: String,
      default: "payment"
    },
    sampleList: {
      type: Array,
      default: () => []
    },
    accountId: {
      type: Number,
      default: null
    },
    accountType: {
      type: String,
      default: "patient"
    },
    showBackButton: {
      type: Boolean,
      default: false
    },
    needSampleId: {
      type: Boolean,
      default: true
    },
    sampleId: {
      type: String,
      default: null
    },
    orderId: {
      type: String,
      default: null
    },
    stripeAccount: {
      type: String,
      default: ""
    },
    showSaveCard: {
      type: Boolean,
      default: false
    },
    clinicId: {
      type: Number,
      default: null
    },
    currency: {
      type: Object,
      default: () => ({ name: "USD", symbol: "$"})
    },
    chargeType: {
      type: String,
      default: "testorder"
    },
    orderPriceList: {
      type: Array,
      default: () => []
    },

    payOrderWithoutApply: {
      type: Boolean,
      default: false
    },
    isUseCustomerCredit: {
      type: Boolean,
      default: false
    },
    customerCredit: {
      type: Number,
      default: 0
    },
    usedCredit: {
      type: Number,
      default: 0
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ["completePayment", "cancelPayment"],
  data() {
    return {
      stripeKey: null,
      completePaymentLoading: false,
      cardholderName: "",
      isSaveCard: false,
      stripePaymentInfo: null
    }
  },
  computed: {
    isBottom() {
      return document.location.href.includes('/ordertest') 
        && !document.location.href.includes('order-center');
    },
  },
  mounted() {
    this.initializeStripe();
  },
  methods: {
    async initializeStripe() {
      // get stripe key
      let stripeKeyResp = await getStripeKey(this.stripeAccount);
      this.stripeKey = stripeKeyResp.data.stripe_key;
      const stripe = window.Stripe(stripeKeyResp.data.stripe_key);
      // let elements = stripe.elements({ appearance, clientSecret });
      let elements = stripe.elements();
      const cardElementStyle = {
        base: {
          color: "#000000",
          fontFamily: "IBM Plex Sans Hebrew, sans-serif",
          fontSize: "16px",
          letterSpacing: "0.15px",
          "::placeholder": {
            color: "#999999",
          },
        }
      };
      const cardNumberElement = elements.create("cardNumber", {
        showIcon: false,
        placeholder: "Credit card number",
        style: cardElementStyle,
        disableLink: true
      });
      const cardExpiryElement = elements.create("cardExpiry", { 
        placeholder: "MM/YY",
        style: cardElementStyle 
      });
      const cardCvvElement = elements.create("cardCvc", { 
        placeholder: "CVC",
        style: cardElementStyle 
      });
      cardNumberElement.mount("#card-number");
      cardExpiryElement.mount("#card-expiry");
      cardCvvElement.mount("#card-cvv");
      let paymentForm = document.querySelector("#payment-info");
      paymentForm.addEventListener('submit', async () => {
        if (this.disabled) return;
        // if user already paid, emit the payment info immediately to avoid duplicate payment
        if (this.stripePaymentInfo) {
          this.$emit('completePayment', this.stripePaymentInfo);
          return;
        }
        try {
          this.completePaymentLoading = true;
          // call stripe create payment method to get payment token
          const createPaymentMethodResp = await stripe.createPaymentMethod({ 
            type: "card", 
            card: cardNumberElement,
            billing_details: {
              name: this.cardholderName,
            }, 
          });
          if (createPaymentMethodResp.error) {
            this.completePaymentLoading = false;
            notify({ type: 'error', title: 'Failed', message: createPaymentMethodResp.error?.message || 'Payment failed. Please try again.' });
            return;
          }
          let paymentToken = createPaymentMethodResp.paymentMethod.id;
          if (this.mode === 'payment') {
            // create paymentIntent
            let recordId = await this.callCreatePaymentIntentApi();
            // call charge api in payment mode
            this.stripePaymentInfo = await this.getStripePaymentInfo(paymentToken, recordId);
            if (this.stripePaymentInfo.nextAction) {
              notify({ type: 'error', title: 'Payment Authentication Required', message: "We're sorry, but we encountered an issue processing your payment. Please try again in a few minutes or use a different credit card. If the problem persists, contact your bank or our support team." });
              this.stripePaymentInfo = null;
              this.completePaymentLoading = false;
              return;
            }
            // save credit card payment method
            if (this.isSaveCard) {
              let { card } = createPaymentMethodResp.paymentMethod;
              await this.callSavePaymentApi(paymentToken, card);
            }
            this.completePaymentLoading = false;
          }
          if (this.mode === 'setup') {
            let { card } = createPaymentMethodResp.paymentMethod;
            await this.callSavePaymentApi(paymentToken, card);
            this.completePaymentLoading = false;
          }
          this.$emit('completePayment', this.stripePaymentInfo);
        } catch (err) {
          console.log(err);
          this.completePaymentLoading = false;
        }
      });
    },

    async callCreatePaymentIntentApi() {
      let recordId = null;
      // create payment intent to get record id
      let token_platform = this.stripeAccount === "ZB" ? "stripe_zb" : "stripe";
      let paymentBody = {
        amount: Number(this.chargeAmount.toFixed(2)),
        currency: this.currency.name.toLowerCase(),
        token_platform,
        payment_type: "card"
      };
      
      let paymentIntentResp = await createPaymentIntent(paymentBody);
      recordId = paymentIntentResp.data.record_id;
      return recordId;
    },
    async getStripePaymentInfo(paymentToken, recordId) {
      let samples = [];
      let orderSamplePriceMaps = { sampleIdJulienBarcodeMap: {}, samplePriceMap: {} };
      if (this.sampleList.length > 0) {
        samples = this.sampleList;
      } else {
        let { sampleIdJulienBarcodeMap, samplePriceMap } = await getOrderSamplePriceMaps(this.orderPriceList);
        Object.keys(sampleIdJulienBarcodeMap).forEach(sampleId => {
          samples.push({
            orderId: null,
            sample_id: sampleId,
            balance: samplePriceMap[sampleId]
          });
        });
        orderSamplePriceMaps = { sampleIdJulienBarcodeMap, samplePriceMap };
      }
      let stripeChargeResp = await this.callPayOrdersApi(paymentToken, recordId, samples);
      // await stripe.confirmCardPayment(stripeChargeResp.data.client_secret);
      let { sample_id, julien_barcode, record_id, next_action } = stripeChargeResp.data;
      let stripePaymentInfo = {
        paymentId: record_id,
        sampleId: sample_id,
        julienBarcode: julien_barcode,
        orderId: stripeChargeResp.orderId,
        platform: "stripe",
        nextAction: next_action,
        orderSamplePriceMaps
      };
      return stripePaymentInfo;
    },

    async callPayOrdersApi(paymentToken, recordId, sampleList) {
      let chargeDetails = getChargeDetailsForPayOrdersApi(sampleList, this.isUseCustomerCredit, this.customerCredit, this.chargeType, this.usedCredit);
      let token_platform = this.stripeAccount === "ZB" ? "stripe_zb" : "stripe";
      let payOrdersResp = await payOrdersApi({
        account_id: this.accountId,
        account_type: this.accountType,
        amount: Number(this.chargeAmount.toFixed(2)),
        currency: this.currency.name.toLowerCase(),
        charge_details: chargeDetails,
        type: 'card',
        token_platform,
        payment_token: paymentToken,
        payment_source: 'portal',
        record_id: recordId,
        without_apply: this.payOrderWithoutApply
      });
      return payOrdersResp;
    },

    async callSavePaymentApi(paymentToken, card) {
      try {
        let saveAchPaymentResp = await savePaymentApi({
          account_id: this.accountId,
          account_type: this.accountType,
          clinic_id: this.clinicId,
          type: 'card',
          token_platform: 'stripe',
          payment_token: paymentToken,
          is_default: false,
          card_type: card.brand,
          last_four: card.last4,
          expiry_date: [card.exp_month, card.exp_year].join(""),
          nick_name: '',
          is_subscription: false,
        });
        return saveAchPaymentResp;
      } catch (err) {
        console.log("call save payment api error:", err);
      }
    },
  }
}
</script>

<style scoped>
.stripe-payment-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.field-row {
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr);
  align-items: start;
  column-gap: 24px;
}

.split-fields-row {
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr) 90px minmax(0, 1fr);
  column-gap: 24px;
  align-items: start;
}

.field-label {
  padding-top: 4px;
  text-align: right;
}

.field-title,
.field-required {
  font-family: Roboto, sans-serif;
  font-size: 14px;
  line-height: 18px;
}

.field-title {
  color: #4a4a4a;
  font-weight: 400;
}

.field-required {
  color: #d10000;
  font-weight: 700;
}

.split-label {
  min-width: 0;
}

.field-shell {
  box-sizing: border-box;
  min-width: 0;
  width: 100%;
  min-height: 44px;
  border: 1px solid #c9cacd;
  border-radius: 4px;
  background: #ffffff;
}

.field-shell-input {
  display: flex;
  align-items: center;
  padding: 0 12px;
}

.field-shell-card {
  overflow: hidden;
  padding: 12px 16px 0;
}

.check-box {
  color: rgba(0, 0, 0, 0.26);
  margin-right: 12px;
}

.action-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.action-back-btn {
  flex-shrink: 0;
  width: 97px;
}

.with-back-button {
  width: calc(100% - 113px);
}

.full-width {
  width: 100%;
}

#submit {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  height: 40px;
  padding: 8px 32px;
  border-radius: 4px;
  border: none;
  background:#004879;
  color: #ffffff;
  font-family: "IBM Plex Sans Hebrew", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.25px;
  transition: background-color 0.5s ease, color 0.5s ease;
  cursor: pointer;
}

#submit:hover {
  background: #20A7C1;
}

#submit:active {
  background: #5587BC;
}

#submit:disabled,
#submit.is-disabled {
  background: #b0b0b0;
  cursor: not-allowed;
  pointer-events: none;
}

.bottom-fixed {
  position: fixed;
  bottom: 89px;
  z-index: 1000;
  width: 373px;
  height: 40px;
  right: 52px;
}

.payment-input {
  width: 100%;
  height: 28px;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  color: #000000;
  font-family: "IBM Plex Sans Hebrew", sans-serif;
  font-size: 16px;
  line-height: 28px;
  letter-spacing: 0.15px;
}

.payment-input::placeholder {
  color: #999999;
  opacity: 1;
}
</style>